import { useState } from "react";
import styled from "styled-components";
import { API } from "aws-amplify";
import { OwcTypography } from "@one/react";
import { LaunchIcon, DownloadIcon, LinkIcon, FileIcon } from "./documentationIcons";
import { GET_IMPORT_FILE_FROM_ENDOR } from "../../gql/bookingapi";
import { REPO_CONFIG } from "../../constants";
import { configureAwsAppSync } from "../../utils/helpers/configureAwsAppSync";
import useAuthenticatedUser from "../../utils/hooks/useAuthenticatedUser";

const Container = styled.div`
  display: flex;
  gap: 24px;
  padding: 16px 0;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const Column = styled.div`
  flex: 1;
  min-width: 0;
  border: 1px solid var(--one-color-border-default, #e0e0e0);
  border-radius: 4px;
  overflow: hidden;
`;

const ColumnHeader = styled.div`
  padding: 8px 16px;
  background: var(--one-color-background-base-2, #f5f5f5);
  border-bottom: 1px solid var(--one-color-border-default, #e0e0e0);
`;

const ListRow = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  gap: 8px;
  min-height: 44px;

  &:not(:last-child) {
    border-bottom: 1px solid var(--one-color-border-default, #e0e0e0);
  }
`;

const Description = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UrlText = styled.a`
  color: var(--one-color-interaction-default-brand-1, #0066cc);
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const ActionIcon = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const FileName = styled.span`
  color: var(--one-color-text-default, #333);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
`;

const EmptyState = styled.div`
  padding: 16px;
  text-align: center;
  color: var(--one-color-text-secondary, #666);
`;

const DocumentationViewSection = ({ checklists }) => {
  const authenticatedUser = useAuthenticatedUser();
  const [downloadingFileIds, setDownloadingFileIds] = useState({});
  const items = checklists || [];

  const handleDownloadFile = async (fileItem) => {
    if (!fileItem.fileId || downloadingFileIds[fileItem.fileId]) {
      return;
    }
    setDownloadingFileIds((prev) => ({ ...prev, [fileItem.fileId]: true }));
    try {
      await configureAwsAppSync({ aws_appsync_graphqlEndpoint: REPO_CONFIG.aws_appsync_graphqlEndpoint });
      const result = await API.graphql({
        query: GET_IMPORT_FILE_FROM_ENDOR,
        variables: {
          targetModule: "REPOSITORY",
          fileId: fileItem.fileId,
          metadata: JSON.stringify({ userId: authenticatedUser?.id || "" }),
        },
      });
      const downloadUrl = result?.data?.getImportFileFromEndor?.url;
      if (downloadUrl) {
        const anchor = document.createElement("a");
        anchor.setAttribute("download", fileItem.fileName);
        anchor.setAttribute("href", downloadUrl);
        anchor.setAttribute("target", "_blank");
        anchor.click();
      }
    } catch (error) {
      console.error("Failed to get download URL", error);
    } finally {
      setDownloadingFileIds((prev) => ({ ...prev, [fileItem.fileId]: false }));
    }
  };

  const linkItems = items.filter((item) => item.documentType === "LINK");
  const fileItems = items.filter((item) => item.documentType === "FILE");

  return (
    <Container>
      <Column>
        <ColumnHeader>
          <OwcTypography variant="subtitle2">Link list</OwcTypography>
        </ColumnHeader>
        {linkItems.length === 0 ? (
          <EmptyState>
            <OwcTypography variant="body2">No links available</OwcTypography>
          </EmptyState>
        ) : (
          linkItems.map((linkItem, index) => (
            <ListRow key={`link-${index}`}>
              <LinkIcon size={18} />
              <Description>{linkItem.description}</Description>
              <UrlText href={linkItem.url} target="_blank" rel="noopener noreferrer">
                {linkItem.url}
              </UrlText>
              <ActionIcon onClick={() => window.open(linkItem.url, "_blank", "noopener,noreferrer")}>
                <LaunchIcon size={18} color="var(--one-color-interaction-default-brand-1, #0066cc)" />
              </ActionIcon>
            </ListRow>
          ))
        )}
      </Column>
      <Column>
        <ColumnHeader>
          <OwcTypography variant="subtitle2">File list</OwcTypography>
        </ColumnHeader>
        {fileItems.length === 0 ? (
          <EmptyState>
            <OwcTypography variant="body2">No files available</OwcTypography>
          </EmptyState>
        ) : (
          fileItems.map((fileItem, index) => (
            <ListRow key={`file-${index}`}>
              <FileIcon size={18} />
              <Description>{fileItem.description}</Description>
              <FileName>{fileItem.fileName}</FileName>
              <ActionIcon
                onClick={() => handleDownloadFile(fileItem)}
                style={downloadingFileIds[fileItem.fileId] ? { opacity: 0.4, pointerEvents: "none" } : undefined}
              >
                <DownloadIcon size={18} color="var(--one-color-interaction-default-brand-1, #0066cc)" />
              </ActionIcon>
            </ListRow>
          ))
        )}
      </Column>
    </Container>
  );
};

export default DocumentationViewSection;
